import { useState, useCallback, useEffect } from 'react';
import { generateMockImages, IMAGES_PER_PAGE, MAX_IMAGES } from '../utils/mockData';
import { imageStorage } from '../utils/storage';

export const useInfiniteScroll = () => {
  const [allImages, setAllImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Initialize: Load from localStorage + generate mock data
  useEffect(() => {
    if (!initialized) {
      // Load user's saved images from localStorage
      const savedImages = imageStorage.getImages();

      // Generate mock data for the feed
      const mockImages = generateMockImages(20);

      // Combine: user images first, then mock data
      const combinedImages = [...savedImages, ...mockImages];

      setAllImages(combinedImages);
      setDisplayedImages(combinedImages.slice(0, IMAGES_PER_PAGE));
      setPage(1);
      setInitialized(true);
    }
  }, [initialized]);

  // Add new images (from user upload)
  const addNewImages = useCallback((newImages) => {
    // Save user-uploaded images to localStorage
    imageStorage.addImages(newImages);

    // Update state
    const updatedImages = [...newImages, ...allImages];
    setAllImages(updatedImages);
    setDisplayedImages(prev => [...newImages, ...prev]);
  }, [allImages]);

  // Load more images (infinite scroll)
  const loadMore = useCallback(() => {
    if (loading) return;

    setLoading(true);

    // Simulate network delay
    setTimeout(() => {
      const startIndex = page * IMAGES_PER_PAGE;
      const endIndex = startIndex + IMAGES_PER_PAGE;

      if (startIndex < allImages.length) {
        // Load from existing images
        const nextImages = allImages.slice(startIndex, endIndex);
        setDisplayedImages(prev => [...prev, ...nextImages]);
        setPage(prev => prev + 1);

        // Generate more mock data if needed
        if (endIndex >= allImages.length) {
          const moreMockImages = generateMockImages(10, allImages.length);
          setAllImages(prev => [...prev, ...moreMockImages]);
        }
      } else {
        // Generate new mock images
        const newMockImages = generateMockImages(IMAGES_PER_PAGE, allImages.length);
        setAllImages(prev => [...prev, ...newMockImages]);
        setDisplayedImages(prev => [...prev, ...newMockImages]);
        setPage(prev => prev + 1);
      }

      // Limit total posts
      if (displayedImages.length >= MAX_IMAGES) {
        setHasMore(false);
      }

      setLoading(false);
    }, 800);
  }, [loading, page, allImages, displayedImages.length]);

  // Remove an image
  const removeImage = useCallback((imageId) => {
    // Remove from localStorage if it's a user image
    imageStorage.removeImage(imageId);

    // Update state
    setAllImages(prev => prev.filter(img => img.id !== imageId));
    setDisplayedImages(prev => prev.filter(img => img.id !== imageId));
  }, []);

  // Clear all user images
  const clearUserImages = useCallback(() => {
    imageStorage.clearImages();

    // Keep only mock images
    const mockOnly = allImages.filter(img => img.id.startsWith('mock-'));
    setAllImages(mockOnly);
    setDisplayedImages(prev => prev.filter(img => img.id.startsWith('mock-')));
  }, [allImages]);

  // Get count of user-uploaded images
  const userImageCount = allImages.filter(img => !img.id.startsWith('mock-')).length;

  return {
    allImages,
    displayedImages,
    loading,
    hasMore,
    userImageCount,
    addNewImages,
    loadMore,
    removeImage,
    clearUserImages,
  };
};
